/**
 * Orgus health (ORGU-182): each of Orgus's own loops as a pulse ring. The arc is the heartbeat's
 * age against its threshold, so a loop running late reads at a glance; the fact sits small below.
 */
import type { OpsHealthState, OpsLoopHeartbeat, OpsLoopStatus } from '@orgus/protocol';
import { OPS_LOOP_LABEL } from '@orgus/protocol';
import { RowStatus, cn, formatRelative, useNow } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { useExt } from '../../lib/store.ts';

const STATUS: Record<OpsLoopStatus, Pick<RowStatusProps, 'tone' | 'icon' | 'label'>> = {
  ok: { tone: 'success', icon: 'done', label: 'On time' },
  late: { tone: 'danger', icon: 'failed', label: 'Late' },
  idle: { tone: 'neutral', icon: 'idle', label: 'Idle' },
  unknown: { tone: 'neutral', icon: 'offline', label: 'No heartbeat' },
};

const RING: Record<OpsLoopStatus, string> = {
  ok: 'text-status-success',
  late: 'text-status-danger',
  idle: 'text-fg-subtle',
  unknown: 'text-fg-subtle',
};

const R = 26;
const C = 2 * Math.PI * R;

/** Share of the threshold the heartbeat's age has used, 0..1. */
export function used(l: OpsLoopHeartbeat, now: number): number {
  if (l.lastAt == null) return 0;
  return Math.min(1, Math.max(0, (now - l.lastAt) / l.thresholdMs));
}

function Pulse({ l, now }: { l: OpsLoopHeartbeat; now: number }) {
  const u = used(l, now);
  return (
    <svg viewBox="0 0 64 64" className={cn('size-16 shrink-0', RING[l.status])} aria-hidden>
      <circle cx="32" cy="32" r={R} fill="none" stroke="currentColor" strokeOpacity={0.15} strokeWidth={5} />
      {l.lastAt != null && <circle cx="32" cy="32" r={R} fill="none" stroke="currentColor" strokeWidth={5} strokeLinecap="round" strokeDasharray={`${Math.max(0.02, u) * C} ${C}`} transform="rotate(-90 32 32)" />}
      <circle cx="32" cy="32" r={l.status === 'ok' ? 5 : 4} fill="currentColor" />
    </svg>
  );
}

function LoopCard({ l, now }: { l: OpsLoopHeartbeat; now: number }) {
  return (
    <div data-loop={l.loop} className="flex items-center gap-3 rounded-card border border-border bg-surface px-3 py-3">
      <Pulse l={l} now={now} />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="flex items-center gap-2">
          <span className="text-body font-medium text-fg">{OPS_LOOP_LABEL[l.loop]}</span>
          <RowStatus {...STATUS[l.status]} focusable={false} className="text-caption" />
        </span>
        <span className="text-caption text-fg-muted">{l.lastAt == null ? 'Never' : formatRelative(l.lastAt, now)}</span>
        <span className="truncate text-caption text-fg-subtle" title={l.fact}>
          {l.fact}
        </span>
      </div>
    </div>
  );
}

export function OpsHealthGrid({ state, now }: { state: OpsHealthState; now: number }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {state.loops.map((l) => (
        <LoopCard key={l.loop} l={l} now={now} />
      ))}
    </div>
  );
}

export function OpsHealthView() {
  const state = useExt<OpsHealthState>('ops-health');
  const now = useNow(15_000);
  if (!state || !state.checkedAt) return <p className="text-body text-fg-subtle">The first check runs within 30 seconds.</p>;
  return <OpsHealthGrid state={state} now={now} />;
}
